import React, { useState, useEffect, useContext } from "react";
import { withStyles } from "@material-ui/core";
import RelatedEntitiesTable from "./Components/RelatedEntitiesTable";
import CreateRelationDialog from "./Components/CreateRelationDialog";
import { LoadingIndicator, NoAccessScreen } from "../../Component";
import ModalDialogContext from "../../Context/ModalDialog/ModalDialogContext";
import {
  getRelatedEntities,
  createRelatedEntity,
  removeRelatedEntity,
} from "../../API/Entity";
import { recordTypes } from "../../API/config";
import styles from "./styles";
import { useTranslation } from "react-i18next";

const RelatedEntities = (props) => {
  const { classes, entity, user, hasRecordPermission, hasPermission } = props;
  const { id: entityId } = entity || {};

  const [isLoading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [relatedEntities, setRelatedEntities] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [relationType, setRelationType] = useState();
  const [canViewRelatedEntities, setCanViewRelatedEntities] = useState();
  const [canAddRelatedEntity, setCanAddRelatedEntity] = useState();
  const [canRemoveRelatedEntity, setCanRemoveRelatedEntity] = useState();

  const { showMessage, showErrorMessage, showLoadingMessage, showConfirmation } = useContext(
    ModalDialogContext
  );
  const { t } = useTranslation();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const result = await getRelatedEntities(entityId);
        const { data = [] } = result || {};
        setRelatedEntities(data);
        setReload(false);
      } catch (error) {
        showErrorMessage({ error });
      }
      setLoading(false);
    };

    if (reload) {
      load();
    }
  }, [reload]);

  useEffect(() => {
    const setPermissions = async () => {
      const { permissions = {} } = entity;
      const canViewRelatedEntities = hasRecordPermission(
        permissions,
        recordTypes.record.actions.viewRelatedEntities.name
      );
      setCanViewRelatedEntities(canViewRelatedEntities);
      const canAddRelatedEntity = hasRecordPermission(
        permissions,
        recordTypes.record.actions.addRelatedEntity.name
      );
      setCanAddRelatedEntity(canAddRelatedEntity);
      const canRemoveRelatedEntity = hasRecordPermission(
        permissions,
        recordTypes.record.actions.removeRelatedEntity.name
      );
      setCanRemoveRelatedEntity(canRemoveRelatedEntity);
    };
    setPermissions();
    setReload(true);
  }, []);

  const openCreateNewDialog = (relationType) => {
    setRelationType(relationType);
    setIsCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setIsCreateDialogOpen(false);
  };

  const deleteRelatedEntity = async ({ id }) => {
    showConfirmation({
      message: t("messages.record.relatedEntities.removeRelatedEntity"),
      onPressYes: async (event) => {
        try {
          showLoadingMessage();
          await removeRelatedEntity({ entityId, relatedEntityId: id });
          showMessage({ message: t("messages.record.relatedEntities.removedRelatedEntity") });
          setReload(true);
        } catch (error) {
          showErrorMessage({ error });
        }
      },
    });
  };

  const submitNew = async (record, source) => {
    try {
      if (source === "Local") {
        const relatedLocalEntityId = record.id;
        await createRelatedEntity({
          entityId,
          relationType,
          relatedLocalEntityId,
        });
      } else if (source === "MDM") {
        const relatedMdmId = record.id;
        const entityName = record.name;
        const address = {};
        if (record.record && record.record.address) {
          address["addressLine1"] = record.record.address["addressLine1"];
          address["addressLine2"] = record.record.address["addressLine2"];
          address["addressLine3"] = record.record.address["addressLine3"];
          address["addressLine4"] = record.record.address["addressLine4"];

          address["city"] = record.record.address["city"];
          address["county"] = record.record.address["countryIso3"];
          address["countryId"] = record.record.address["countryId"];
          address["postCode"] = record.record.address["postalCode"];
        }

        await createRelatedEntity({
          entityId,
          relationType,
          relatedMdmId,
          entityName,
          ...address,
        });
      } else if (source === "DNB") {
        const entityName = record.name;
        const relatedDnbId = record.dunsNumber;

        const address = {};
        if (record.record && record.record.address) {
          address["addressLine1"] = record.record.address["addressLine1"];
          address["addressLine2"] = record.record.address["addressLine2"];
          address["addressLine3"] = record.record.address["addressLine3"];
          address["addressLine4"] = record.record.address["addressLine4"];

          address["city"] = record.record.address["city"];
          address["county"] = record.record.address["countryIso3"];
          address["countryId"] = record.record.address["countryId"];
          address["postCode"] = record.record.address["postalCode"];
        }

        await createRelatedEntity({
          entityId,
          relationType,
          relatedDnbId,
          entityName,
          ...address,
        });
      } else {
        await createRelatedEntity({ entityId, relationType, ...record });
      }
      showMessage({ message: t("messages.record.relatedEntities.createdRelatedEntity") });
      setReload(true);
    } catch (error) {
      showErrorMessage({ error });
    }
    setIsCreateDialogOpen(false);
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  if (!canViewRelatedEntities) {
    return <NoAccessScreen />;
  }

  if (isLoading) {
    return renderLoadingIndicator();
  }

  return (
    <div className={classes.pageContent}>
      <RelatedEntitiesTable
        data={relatedEntities}
        canAddRelatedEntity={canAddRelatedEntity}
        canRemoveRelatedEntity={canRemoveRelatedEntity}
        onCreateNew={openCreateNewDialog}
        onRowDelete={deleteRelatedEntity}
      />
      {isCreateDialogOpen && (
        <CreateRelationDialog
          isDialogOpen={isCreateDialogOpen}
          user={user}
          onSubmit={submitNew}
          onClose={closeCreateDialog}
          hasPermission={hasPermission}
        />
      )}
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(RelatedEntities);
